import type { PlainDate } from "./types";

/**
 * Арифметика по чистым датам 'YYYY-MM-DD'. Никаких `Date` с локальной зоной внутри:
 * `new Date('2026-08-05')` парсится как UTC-полночь, а `new Date(2026, 7, 5)` — как локальная,
 * и на этой паре расходятся ровно те сутки, ради которых заведена настройка таймзоны.
 * Здесь всё считается через `Date.UTC` и обратно, поэтому смещение не участвует вообще.
 */

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isPlainDate(value: string): boolean {
	const m = DATE_RE.exec(value);
	if (!m) {
		return false;
	}
	return formatDate(toUtc(value)) === value;
}

function toUtc(date: PlainDate): number {
	const m = DATE_RE.exec(date);
	if (!m) {
		throw new Error(`невалидная дата: ${date}`);
	}
	return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function formatDate(ms: number): PlainDate {
	const d = new Date(ms);
	const y = String(d.getUTCFullYear()).padStart(4, "0");
	const mo = String(d.getUTCMonth() + 1).padStart(2, "0");
	const day = String(d.getUTCDate()).padStart(2, "0");
	return `${y}-${mo}-${day}`;
}

/** 0 = воскресенье … 6 = суббота (как у `Date#getDay`). */
export function weekday(date: PlainDate): number {
	return new Date(toUtc(date)).getUTCDay();
}

export function addDays(date: PlainDate, days: number): PlainDate {
	return formatDate(toUtc(date) + days * 86_400_000);
}

export function diffDays(from: PlainDate, to: PlainDate): number {
	return Math.round((toUtc(to) - toUtc(from)) / 86_400_000);
}

export function compareDates(a: PlainDate, b: PlainDate): number {
	// Формат отсортирован лексикографически, сравнивать числа незачем.
	return a < b ? -1 : a > b ? 1 : 0;
}

/** Неделя начинается с понедельника (spec-tasks Область 6). */
export function startOfWeek(date: PlainDate): PlainDate {
	const wd = weekday(date);
	const backToMonday = (wd + 6) % 7;
	return addDays(date, -backToMonday);
}

export function endOfWeek(date: PlainDate): PlainDate {
	return addDays(startOfWeek(date), 6);
}

export function startOfMonth(date: PlainDate): PlainDate {
	return `${date.slice(0, 7)}-01`;
}

export function daysInMonth(date: PlainDate): number {
	const m = DATE_RE.exec(date);
	if (!m) {
		throw new Error(`невалидная дата: ${date}`);
	}
	return new Date(Date.UTC(Number(m[1]), Number(m[2]), 0)).getUTCDate();
}

export function endOfMonth(date: PlainDate): PlainDate {
	return `${date.slice(0, 7)}-${String(daysInMonth(date)).padStart(2, "0")}`;
}

export function dayOfMonth(date: PlainDate): number {
	return Number(date.slice(8, 10));
}

/**
 * «Какой сейчас день» в таймзоне системы. Единственный источник «сегодня» —
 * ни на сервере, ни на клиенте локальные часы не спрашиваются (engineering §7).
 */
export function todayIn(tz: string, now: Date = new Date()): PlainDate {
	// en-CA форматирует как YYYY-MM-DD — единственная локаль, дающая ISO-порядок без сборки руками.
	return new Intl.DateTimeFormat("en-CA", {
		timeZone: tz,
		year: "numeric",
		month: "2-digit",
		day: "2-digit"
	}).format(now);
}
